import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, HandArrowDown, Warning, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty } from "./registerApi";

const snapshotLabel = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) + " IST"
    : "never";

// Arrivals are never typed. An item appears the moment SAP shows stock of a
// MAPPED material standing in the site's RM store; the department then CLAIMS
// how much walks to the line, which moves it out of RM into its own godown.
export const ArrivalsPanel = ({ processId, focusProductId, onClaimed }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [values, setValues] = useState({});
  const [confirming, setConfirming] = useState(null);
  const [claiming, setClaiming] = useState(null);

  const load = useCallback(async (live = false) => {
    live ? setRefreshing(true) : setLoading(true);
    try {
      setData(await api.arrivals(processId, live));
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [processId]);

  useEffect(() => { load(false); }, [load]);

  const claim = async (row) => {
    const quantity = Number(values[row.product_id] || 0);
    if (!quantity) {
      toast.error("Enter how much came to the line first");
      return;
    }
    setClaiming(row.product_id);
    try {
      const res = await api.claimHandover({
        process_id: processId,
        product_id: row.product_id,
        quantity,
        sap_site_stock: row.sap_site_stock,
        source_area: data?.rm_area,
      });
      setValues((v) => ({ ...v, [row.product_id]: "" }));
      setConfirming(null);
      if (res.movement?.sap_gac_id) {
        toast.success(`${qty(quantity)} ${row.unit_code} moved ${res.movement.from} → ${res.movement.to} in SAP`, {
          description: `SAP movement ${res.movement.sap_gac_id} - the stock now stands in this department's godown.`,
        });
      } else if (res.over_claim) {
        toast.warning(`Claimed ${qty(quantity)} ${row.unit_code} - flagged for the store`, {
          description: `SAP shows only ${qty(row.sap_site_stock)} at ${data.rm_area}. The claim is kept; the store reconciles it.`,
        });
      } else {
        toast.success(`${qty(quantity)} ${row.unit_code} of ${row.product_id} is now with this department`);
      }
      onClaimed?.(res.register);
      // Read RM straight from SAP so the figure shown is what is left there.
      load(true);
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-sm text-[#667085]" data-testid="arrivals-loading">Reading SAP stock...</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="arrivals-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-[#667085]">
          Raw material store <span className="font-mono font-bold text-[#344054]">{data.rm_area}</span>
          {data.department_area && (
            <> · claims move it into <span className="font-mono font-bold text-[#344054]">{data.department_area}</span></>
          )} · snapshot {snapshotLabel(data.snapshot_at)}
          {data.source === "cache_after_live_failed" && (
            <span className="text-[#B54708] font-bold"> · live read failed, showing the last snapshot</span>
          )}
        </p>
        <Button size="sm" variant="outline" disabled={refreshing} onClick={() => load(true)} data-testid="arrivals-refresh-live">
          <ArrowClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Reading SAP..." : "Refresh from SAP"}
        </Button>
      </div>

      {data.items.length === 0 && (
        <div className="rounded-sm border border-[#D0D5DD] bg-white p-6 text-center text-sm text-[#667085]">
          No materials are mapped to this process yet.
        </div>
      )}

      {data.items.map((row) => (
        <div
          key={row.product_id}
          className={`bg-white border rounded-sm p-3 space-y-2 ${row.product_id === focusProductId ? "border-[#B54708] ring-1 ring-[#FEDF89]" : "border-[#D0D5DD]"}`}
          data-testid={`arrival-${row.product_id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-sm font-bold text-[#101828] truncate">{row.product_id}</div>
              <div className="text-xs text-[#667085] truncate">{row.description || "—"}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-bold tabular-nums text-[#1D2939] leading-none">{qty(row.sap_site_stock)}</div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#667085]">{row.unit_code} at {data.rm_area}</div>
            </div>
          </div>

          {!row.arrived && (
            <p className="text-[11px] text-[#B54708] flex items-center gap-1" data-testid={`arrival-empty-${row.product_id}`}>
              <Package size={13} weight="bold" /> Nothing at {data.rm_area} right now
            </p>
          )}
          {row.locations.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid={`arrival-godowns-${row.product_id}`}>
              {row.locations.map((loc, idx) => (
                <span
                  key={idx}
                  className="text-[11px] rounded-sm px-1.5 py-0.5 border bg-[#F9FAFB] border-[#EAECF0] text-[#475467]"
                  data-testid={`arrival-godown-${row.product_id}-${loc.area_id}`}
                >
                  {loc.logistics_area || loc.logistics_area_id} · {qty(loc.qty)}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="any"
              max={row.sap_site_stock}
              inputMode="decimal"
              className="h-12 text-right text-xl font-bold tabular-nums"
              placeholder="Taken to the line"
              value={values[row.product_id] ?? ""}
              onChange={(e) => { setValues((v) => ({ ...v, [row.product_id]: e.target.value })); setConfirming(null); }}
              data-testid={`claim-input-${row.product_id}`}
            />
            <Button
              className="h-12 px-4 shrink-0"
              disabled={claiming === row.product_id || !row.arrived}
              onClick={() => setConfirming(row.product_id)}
              data-testid={`claim-button-${row.product_id}`}
            >
              <HandArrowDown size={16} weight="bold" /> Claim
            </Button>
          </div>

          {confirming === row.product_id && (
            <div className="space-y-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-2" data-testid={`claim-confirm-${row.product_id}`}>
              <p className="text-xs text-[#B54708]">
                This posts a SAP movement: {qty(Number(values[row.product_id] || 0))} {row.unit_code} of{" "}
                <span className="font-mono font-bold">{row.product_id}</span> out of{" "}
                <span className="font-mono font-bold">{data.rm_area}</span> into{" "}
                <span className="font-mono font-bold">{data.department_area}</span>, right now.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 bg-[#0E7C86] hover:bg-[#0B6B74]"
                  disabled={claiming === row.product_id}
                  onClick={() => claim(row)}
                  data-testid={`claim-confirm-yes-${row.product_id}`}
                >
                  {claiming === row.product_id ? "Moving in SAP..." : "Yes, claim and move it"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirming(null)} data-testid={`claim-confirm-cancel-${row.product_id}`}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-[11px] text-[#667085]">
            <span>claimed this week {qty(row.claimed_this_week)} {row.unit_code}</span>
            {Number(values[row.product_id] || 0) > row.sap_site_stock && (
              <span className="text-[#B42318] font-bold flex items-center gap-1">
                <Warning size={12} weight="bold" /> more than {data.rm_area} holds - SAP will refuse it
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
